import React, { useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { askAIAssistant } from '../../ApiServices/ApiServices';
import ReactMarkdown from 'react-markdown';
import './AIChatbotButton.css';

const INITIAL_BOT_MESSAGES = [
    { role: 'bot', content: 'Hi there!' },
    { role: 'bot', content: "I'm CodeQueue Assistant" },
    { role: 'bot', content: 'How can I help you today?' }
];

// Role-based default questions
const DEFAULT_QUESTIONS = {
    mentor: [
        'Manage Sessions',
        'Start Mentoring',
        'Offer Services',
        'Improve Profile',
        'View Bookings'
    ],
    mentee: [
        'What is CodeQueue?',
        'How it works',
        'Career Growth Help',
        'Interview Preparation',
        'Explore Mentors'
    ],
    general: [
        'Book a Mentor',
        'Resume Help',
        'Mock Interviews',
        'Choose Mentor',
        'Interview Prep'
    ]
};

const normalizeUserRole = (rawRole) => {
    const role = String(rawRole || '').toLowerCase();

    if (role === 'mentor') return 'mentor';
    if (role === 'mentee' || role === 'user' || role === 'student' || role === 'candidate') return 'mentee';

    return 'general';
};

const AIChatbotButton = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState(() => {
        const token = localStorage.getItem('token');
        if (!token) return INITIAL_BOT_MESSAGES;

        try {
            const savedMessages = localStorage.getItem('assistantMessages');
            if (!savedMessages) return INITIAL_BOT_MESSAGES;

            const parsedMessages = JSON.parse(savedMessages);
            return Array.isArray(parsedMessages) && parsedMessages.length > 0
                ? parsedMessages
                : INITIAL_BOT_MESSAGES;
        } catch {
            return INITIAL_BOT_MESSAGES;
        }
    });
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState('general');
    const [previousConversation, setPreviousConversation] = useState(() => {
        const token = localStorage.getItem('token');
        if (!token) return [];

        try {
            const savedConversation = localStorage.getItem('assistantConversation');
            if (!savedConversation) return [];

            const parsedConversation = JSON.parse(savedConversation);
            return Array.isArray(parsedConversation) ? parsedConversation : [];
        } catch {
            return [];
        }
    });
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);

    // const sanitizeResponse = (text) => {
    //     return text
    //         .replace(/\*\*/g, '') 
    //         .replace(/\*\*/g, '') 
    //         .replace(/^[\s*]+|[\s*]+$/g, '');
    // };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                const role = decoded.role || decoded.userType || decoded.user_role || 'general';
                setUserRole(normalizeUserRole(role));
            } catch (error) {
                console.log('Token decoding error:', error);
                setUserRole('general');
            }
        }
        // Check if the popup was closed 
        const isAiPopupOpened = localStorage.getItem('isAiPopupOpened');
        if (isAiPopupOpened) {
            setOpen(!isAiPopupOpened);
        }
    }, []);

    useEffect(() => {
        if (localStorage.getItem('token') && previousConversation.length > 0) {
            localStorage.setItem('assistantConversation', JSON.stringify(previousConversation));
        }
    }, [previousConversation]);

    useEffect(() => {
        if (localStorage.getItem('token') && messages.length > 0) {
            localStorage.setItem('assistantMessages', JSON.stringify(messages));
        }
    }, [messages]);

    useEffect(() => {
        const handleStorageChange = () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setPreviousConversation([]);
                localStorage.removeItem('assistantConversation');
                localStorage.removeItem('assistantMessages');
                setMessages(INITIAL_BOT_MESSAGES);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages]);

    const cleanupConversationHistory = (conversation) => {
        const trimmedConversation = [...conversation];
        while (trimmedConversation.length > 20) {
            trimmedConversation.shift();
            if (trimmedConversation.length > 0) {
                trimmedConversation.shift();
            }
        }
        return trimmedConversation;
    };

    const cleanupUiMessages = (allMessages) => {
        const botIntroCount = 3;
        const staticIntro = allMessages.slice(0, botIntroCount);
        const chatOnlyMessages = [...allMessages.slice(botIntroCount)];

        while (chatOnlyMessages.length > 20) {
            chatOnlyMessages.shift();
            if (chatOnlyMessages.length > 0) {
                chatOnlyMessages.shift();
            }
        }

        return [...staticIntro, ...chatOnlyMessages];
    };

    const handleSendMessage = async (messageToSend = inputValue) => {
        const userMessage = messageToSend.trim();

        if (!userMessage) return;

        setInputValue('');
        setMessages(prev => cleanupUiMessages([...prev, { role: 'user', content: userMessage }]));

        let updatedConversation = [
            ...previousConversation,
            { role: 'user', content: userMessage }
        ];

        updatedConversation = cleanupConversationHistory(updatedConversation);
        setPreviousConversation(updatedConversation);

        setLoading(true);

        try {
            const response = await askAIAssistant(updatedConversation, userRole);

            if (response.success) {
                const assistantResponse =(response.ans);
                setMessages(prev => cleanupUiMessages([...prev, { role: 'bot', content: assistantResponse }]));

                let finalConversation = [
                    ...updatedConversation,
                    { role: 'assistant', content: assistantResponse }
                ];

                finalConversation = cleanupConversationHistory(finalConversation);
                setPreviousConversation(finalConversation);
            } else {
                setMessages(prev => [...prev, { 
                    role: 'bot', 
                    content: 'Sorry, I encountered an error. Please try again.' 
                }]);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setMessages(prev => [...prev, { 
                role: 'bot', 
                content: 'Sorry, I encountered an error. Please check your connection and try again.' 
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleQuestionClick = (question) => {
        if (loading) return;
        handleSendMessage(question);
    };

    const getDefaultQuestions = () => {
        return DEFAULT_QUESTIONS[userRole] || DEFAULT_QUESTIONS.general;
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleSendMessage();
        }
    };

    const handleCloseAiPopup = () => {
        localStorage.setItem('isAiPopupOpened', true);
        setOpen(false);
    }

    return (
        <div className={`ai-chatbot-wrapper${open ? ' is-open' : ''}`}>
            {/* {!open && (
                <div className="ai-chat-bubble">Ask AI</div>
            )} */}

            {open && (
                <div className="ai-popup">
                    <div className="ai-popup-gradient">
                        <div className="ai-popup-header">
                            <div className="ai-popup-icon">
                                <img src="/asset/aichatbot.png" alt="AI" className="ai-popup-body" />
                            </div>
                            <button className="ai-popup-close" onClick={handleCloseAiPopup} aria-label="Close">
                                ✕
                            </button>
                        </div>
                        <div className="ai-popup-messages" ref={messagesContainerRef}>
                            {messages.map((msg, idx) => (
                                <div
                                    key={idx}
                                    className={msg.role === 'bot' ? 'ai-msg-bot' : 'ai-msg-user'}
                                >
                                    {msg.role === 'bot'
                                        ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                                        : msg.content}
                                </div>
                            ))}
                            {loading && (
                                <div className="ai-msg-bot ai-loading">
                                    Thinking<span>.</span><span>.</span><span>.</span>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                    <div className="ai-popup-footer">
                        <div className="ai-popup-input">
                            <input
                                type="text"
                                placeholder="Start typing !"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading}
                            />
                            <button
                                className="ai-send-btn"
                                onClick={handleSendMessage}
                                disabled={loading || !inputValue.trim()}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="11"
                                        stroke={loading || !inputValue.trim() ? '#888' : '#4130E0'}
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        d="M10 8l4 4-4 4"
                                        stroke={loading || !inputValue.trim() ? '#888' : '#4130E0'}
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="ai-popup-suggestions">
                            {getDefaultQuestions().map((question, idx) => (
                                <button
                                    key={idx}
                                    className="ai-suggestion-chip"
                                    onClick={() => handleQuestionClick(question)}
                                    title={question}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {!open && (
    <button
        className="ai-chatbot-btn"
        aria-label="Open AI Chatbot"
        onClick={() => setOpen(true)}
    >
        <div className="ai-icon-container">
            <img
                src="/asset/aichatbot-body.png"
                alt="AI Chatbot"
                className="ai-body"
            />
            <img
                src="/asset/aichatbot-eyes.png"
                alt=""
                className="ai-eyes"
                aria-hidden="true"
            />
        </div>
    </button>
)}
        </div>
    );
};

export default AIChatbotButton;
